import { lazy, Suspense, useEffect, useState, type ComponentType } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { CircleCheck, House, ListChecks, RotateCcw, User, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { locale, m } from '@/lib/i18n';
import { useAppState, useLiveSync, useOnline } from '@/lib/queries';
import { syncPush } from '@/lib/push';
import { cn } from '@/lib/utils';
import { Today } from '@/views/Today';
import { GrowthWatcher } from '@/components/GrowthWatcher';
// Other screens are split into their own files so the first load stays small,
// then fetched in the background right after start. Once a screen's code is in,
// it renders straight away: React.lazy would still pause for a tick on first use.
function screen<P extends object>(load: () => Promise<ComponentType<P>>) {
  let loaded: ComponentType<P> | null = null;
  const preload = () => load().then((c) => (loaded = c));
  const Lazy = lazy(() => preload().then((c) => ({ default: c })));
  function Screen(props: P) {
    const Comp = loaded ?? Lazy;
    return <Comp {...props} />;
  }
  return Object.assign(Screen, { preload });
}
const Onboarding = screen(() => import('@/views/Onboarding').then((mod) => mod.Onboarding));
const HouseView = screen(() => import('@/views/House').then((mod) => mod.HouseView));
const Chores = screen(() => import('@/views/Chores').then((mod) => mod.Chores));
const Profile = screen(() => import('@/views/Profile').then((mod) => mod.Profile));
const Settings = screen(() => import('@/views/Settings').then((mod) => mod.Settings));
const InstallScreen = screen(() => import('@/views/InstallScreen').then((mod) => mod.InstallScreen));
const preloadScreens = () => [HouseView, Chores, Profile, Settings].forEach((s) => s.preload().catch(() => {}));
import { shouldShowApp } from '@/lib/install';

export type Tab = 'today' | 'house' | 'chores' | 'profile';
const TABS: { id: Tab; icon: typeof House; label: () => string }[] = [
  { id: 'today', icon: CircleCheck, label: () => m.tab_today() },
  { id: 'house', icon: House, label: () => m.tab_house() },
  { id: 'chores', icon: ListChecks, label: () => m.tab_chores() },
  { id: 'profile', icon: User, label: () => m.tab_profile() },
];

/** Tab and sub-page live in the URL so a reload (or a notification link) lands in the right place. */
function useRoute() {
  const read = () => {
    const p = new URLSearchParams(location.search);
    const tab = (TABS.some((t) => t.id === p.get('tab')) ? p.get('tab') : 'today') as Tab;
    return { tab, page: p.get('page'), view: p.get('view'), focus: p.get('focus') };
  };
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const onPop = () => setRoute(read());
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);
  /** Open a tab (and sub-page); `focus` scrolls to a section on arrival, e.g. Settings' reminders. */
  const go = (tab: Tab, page: string | null = null, focus: string | null = null) => {
    const p = new URLSearchParams();
    if (tab !== 'today') p.set('tab', tab);
    if (page) p.set('page', page);
    if (focus) p.set('focus', focus);
    const url = p.size ? `?${p}` : location.pathname;
    history.pushState(null, '', url);
    setRoute({ tab, page, view: null, focus });
    scrollTo({ top: 0 });
  };
  return { ...route, go };
}

export function App() {
  const state = useAppState();
  const online = useOnline();
  const route = useRoute();
  const [showApp, setShowApp] = useState(shouldShowApp);
  const data = state.data;
  useLiveSync(!!data);

  // Keep the server in step with this device: notifications go out in its language.
  useEffect(() => {
    if (data && data.me.locale !== locale) api('PATCH', '/me', { locale }).catch(() => {});
  }, [data?.me.locale]);
  // The home-screen icon shows how many of today's chores are still waiting.
  const waiting = data ? data.dailies.filter((d) => d.status === 'pending').length : 0;
  useEffect(() => {
    const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (!nav.setAppBadge || !data) return;
    (waiting ? nav.setAppBadge(waiting) : nav.clearAppBadge!()).catch(() => {});
  }, [waiting, !!data]);

  useEffect(() => {
    if (!data) return;
    preloadScreens();
    void syncPush().then((changed) => {
      if (changed) void state.refetch();
    });
  }, [!!data]);

  let body;
  if (!showApp) body = <InstallScreen onContinue={() => setShowApp(true)} />;
  else if (state.isPending) body = <BootSkeleton />;
  else if (state.isError && !data) body = <LoadError message={(state.error as Error).message} retry={() => state.refetch()} retrying={state.isFetching} />;
  else if (!data) body = <Onboarding />;
  else {
    const page =
      route.tab === 'profile' && route.page === 'settings' ? (
        <Settings state={data} onBack={() => history.back()} focus={route.focus} />
      ) : route.tab === 'house' ? (
        <HouseView state={data} openRecap={route.view === 'recap'} />
      ) : route.tab === 'chores' ? (
        <Chores state={data} />
      ) : route.tab === 'profile' ? (
        <Profile state={data} openSettings={() => route.go('profile', 'settings')} />
      ) : (
        <Today state={data} go={route.go} openRecap={route.view === 'recap'} />
      );
    body = (
      <>
        {/* Screens swap instantly and fade in quickly: no sliding, no waiting for the old one to leave. */}
        <motion.main
          key={`${route.tab}/${route.page ?? ''}`}
          className="grid gap-7"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.14, ease: 'easeOut' }}
        >
          {/* Only the page waits for its code (first visit, before preloading finishes); the tab bar stays put. */}
          <Suspense fallback={<PageFallback />}>{page}</Suspense>
        </motion.main>
        <TabBar tab={route.tab} go={route.go} />
        <GrowthWatcher state={data} />
      </>
    );
  }

  return (
    <div className="mx-auto max-w-[520px] px-5 pt-[calc(20px+env(safe-area-inset-top))] pb-[calc(110px+env(safe-area-inset-bottom))]">
      <AnimatePresence>
        {!online && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            role="status"
            className="bg-foreground text-background fixed top-[calc(10px+env(safe-area-inset-top))] left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-lg"
          >
            <WifiOff className="size-4" /> {m.offline_banner()}
          </motion.div>
        )}
      </AnimatePresence>
      <Suspense fallback={<BootSkeleton />}>{body}</Suspense>
    </div>
  );
}

// One spring for the whole tab bar, so the pill and the labels move as one.
const TAB_SPRING = { type: 'spring', stiffness: 420, damping: 36, mass: 0.8 } as const;

function TabBar({ tab, go }: { tab: Tab; go: (t: Tab) => void }) {
  return (
    <nav
      aria-label={m.sections()}
      className="bg-card/85 fixed bottom-[calc(14px+env(safe-area-inset-bottom))] left-1/2 z-40 flex -translate-x-1/2 gap-1 rounded-full border p-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.08)] backdrop-blur-xl"
    >
      {TABS.map(({ id, icon: Icon, label }) => {
        const active = tab === id;
        return (
          <motion.button
            key={id}
            onClick={() => go(id)}
            whileTap={{ scale: 0.94 }}
            aria-label={label()}
            aria-current={active ? 'page' : undefined}
            className={cn('relative flex h-12 items-center rounded-full px-4 text-sm font-semibold transition-colors duration-200', active ? 'text-background' : 'text-muted-foreground')}
          >
            {active && <motion.span layoutId="tab-pill" className="bg-foreground absolute inset-0 rounded-full" transition={TAB_SPRING} />}
            <Icon className="relative size-[22px] shrink-0" />
            {/* The label grows in while the previous one shrinks out, so the bar never jumps. */}
            <AnimatePresence initial={false}>
              {active && (
                <motion.span
                  key="label"
                  className="relative overflow-hidden whitespace-nowrap"
                  initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                  animate={{ width: 'auto', opacity: 1, marginLeft: 8, transition: { ...TAB_SPRING, opacity: { duration: 0.18, delay: 0.05 } } }}
                  exit={{ width: 0, opacity: 0, marginLeft: 0, transition: { ...TAB_SPRING, opacity: { duration: 0.1 } } }}
                >
                  {label()}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        );
      })}
    </nav>
  );
}

/** A calm placeholder while a screen's code arrives; it only appears after a short delay. */
function PageFallback() {
  return (
    <motion.div className="grid gap-4 pt-2" initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.3 } }} aria-busy="true" aria-label={m.loading()}>
      <Skeleton className="h-9 w-40" />
      <Skeleton className="h-40 rounded-3xl" />
    </motion.div>
  );
}

function BootSkeleton() {
  return (
    <div className="grid gap-7 pt-2" aria-busy="true" aria-label={m.loading()}>
      <div className="grid gap-2">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-5 w-56" />
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 7 }, (_, i) => (
          <Skeleton key={i} className="mx-auto size-9 rounded-full" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-3xl" />
      <Skeleton className="h-24 rounded-2xl" />
    </div>
  );
}

function LoadError({ message, retry, retrying }: { message: string; retry: () => void; retrying: boolean }) {
  return (
    <div className="grid min-h-[70dvh] place-content-center gap-4 text-center">
      <h1 className="text-2xl font-bold">{m.load_error_title()}</h1>
      <p className="text-muted-foreground max-w-xs">{message}</p>
      <Button size="lg" onClick={retry} disabled={retrying}>
        <RotateCcw className={cn(retrying && 'animate-spin')} /> {m.try_again()}
      </Button>
    </div>
  );
}
